import React, { useState, useEffect } from 'react';
import { Booking, BookingStatus, PaymentStatus } from '../../types/booking';
import { VehicleStatus } from '../../types/vehicle';
import { bookingService } from '../../services/bookingService';
import { vehicleService } from '../../services/vehicleService';
import { extrasService, Extra } from '../../services/extrasService';
import { discountService } from '../../services/discountService';
import { apiService } from '../../services/api';
import Modal from '../Common/Modal';

interface ManualBookingFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (booking: Booking) => void;
}

const ManualBookingForm: React.FC<ManualBookingFormProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [extras, setExtras] = useState<Extra[]>([]);
  const [selectedExtras, setSelectedExtras] = useState<string[]>([]);
  const [selectedCar, setSelectedCar] = useState<any>(null);
  const [mileageType, setMileageType] = useState<'limited' | 'unlimited'>('limited');
  const [lookingUpCustomer, setLookingUpCustomer] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  const [discountInfo, setDiscountInfo] = useState<any>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    // Customer Info
    customerFirstName: '',
    customerLastName: '',
    customerEmail: '',
    customerPhone: '',
    customerLicenseNumber: '',
    
    // Booking Details
    carId: '',
    pickUpDate: '',
    pickUpTime: '09:00',
    returnDate: '',
    returnTime: '09:00',
    pickUpLocation: 'East Perth',
    returnLocation: 'East Perth',
    
    // Payment
    totalAmount: 0,
    paidAmount: 0,
    paymentMethod: 'counter',
    paymentStatus: PaymentStatus.PENDING,
    
    // Additional
    driverAge: 25,
    specialRequests: '',
    internalNotes: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadVehicles();
      loadExtras();
    }
  }, [isOpen]);

  const loadExtras = async () => {
    try {
      const response = await extrasService.getAllExtras();
      if (response.success && response.data) {
        setExtras(response.data);
      }
    } catch (err) {
      console.error('Failed to load extras:', err);
    }
  };

  // Auto-calculate return date when pickup date changes
  useEffect(() => {
    if (formData.pickUpDate) {
      const pickupDate = new Date(formData.pickUpDate);
      pickupDate.setDate(pickupDate.getDate() + 7); // Add 7 days
      const returnDateStr = pickupDate.toISOString().split('T')[0];
      setFormData(prev => ({
        ...prev,
        returnDate: returnDateStr,
        returnTime: prev.pickUpTime // Same time as pickup
      }));
    }
  }, [formData.pickUpDate, formData.pickUpTime]);

  // Auto-calculate total amount when dates, mileage type, extras, or discount change
  useEffect(() => {
    if (formData.pickUpDate && formData.returnDate && selectedCar) {
      calculateTotalAmount();
    }
  }, [formData.pickUpDate, formData.returnDate, mileageType, selectedCar, selectedExtras, discountInfo]);

  const loadVehicles = async () => {
    try {
      const response = await vehicleService.getAllVehicles();
      if (response.success && response.data) {
        setVehicles(response.data);
      }
    } catch (err) {
      console.error('Failed to load vehicles:', err);
    }
  };

  // Lookup customer by license number
  const handleLicenseBlur = async () => {
    if (!formData.customerLicenseNumber || formData.customerLicenseNumber.length < 3) return;
    
    setLookingUpCustomer(true);
    try {
      const response = await apiService.get(`/api/customers?licenseNumber=${formData.customerLicenseNumber}`);
      if (response.success && response.data && Array.isArray(response.data) && response.data.length > 0) {
        const customer = response.data[0];
        setFormData(prev => ({
          ...prev,
          customerFirstName: customer.firstName || prev.customerFirstName,
          customerLastName: customer.lastName || prev.customerLastName,
          customerEmail: customer.email || prev.customerEmail,
          customerPhone: customer.contact || customer.phone || prev.customerPhone
        }));
      }
    } catch (err) {
      console.log('Customer not found, will create new');
    } finally {
      setLookingUpCustomer(false);
    }
  };

  // Handle car selection
  const handleCarChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const carId = e.target.value;
    setFormData(prev => ({ ...prev, carId }));
    
    const car = vehicles.find(v => v._id === carId);
    setSelectedCar(car);
  };

  // Calculate total amount based on rental duration
  const calculateTotalAmount = () => {
    if (!formData.pickUpDate || !formData.returnDate || !selectedCar) return;
    
    const pickup = new Date(formData.pickUpDate);
    const returnD = new Date(formData.returnDate);
    let days = Math.ceil((returnD.getTime() - pickup.getTime()) / (1000 * 60 * 60 * 24));
    
    // If same day or 0 days, treat as 1 day rental
    if (days <= 0) days = 1;
    
    // Find the correct rate tier based on rental duration
    let rate = 0;
    if (mileageType === 'limited') {
      // Find the matching rate tier for the number of days
      const rateTier = selectedCar.rates?.find((r: any) => 
        days >= r.minDays && (r.maxDays === null || days <= r.maxDays)
      );
      rate = rateTier?.rate || selectedCar.defaultRatePerDay || 0;
    } else {
      // Find the matching unlimited rate tier for the number of days
      const rateTier = selectedCar.unlimedRates?.find((r: any) => 
        days >= r.minDays && (r.maxDays === null || days <= r.maxDays)
      );
      rate = rateTier?.rate || selectedCar.defaultUnlimitedRatePerDay || 0;
    }
    
    const carTotal = days * rate;
    
    // Calculate extras total
    const extrasTotal = selectedExtras.reduce((sum, extraId) => {
      const extra = extras.find(e => e._id === extraId);
      return sum + (extra?.price || 0);
    }, 0);
    
    let total = carTotal + extrasTotal;
    
    // Apply discount if valid
    if (discountInfo && discountInfo.isValid) {
      total = discountInfo.finalAmount;
    }
    
    setFormData(prev => ({ ...prev, totalAmount: total }));
  };

  // Validate discount code
  const validateDiscountCode = async () => {
    if (!discountCode.trim()) {
      setDiscountError('Please enter a discount code');
      return;
    }

    if (!formData.pickUpDate || !formData.returnDate || !selectedCar) {
      setDiscountError('Please select dates and vehicle first');
      return;
    }

    setValidatingDiscount(true);
    setDiscountError(null);

    try {
      const pickup = new Date(formData.pickUpDate);
      const returnD = new Date(formData.returnDate);
      const days = Math.ceil((returnD.getTime() - pickup.getTime()) / (1000 * 60 * 60 * 24)) || 1;
      
      // Calculate original amount before discount
      let rate = 0;
      if (mileageType === 'limited') {
        const rateTier = selectedCar.rates?.find((r: any) => 
          days >= r.minDays && (r.maxDays === null || days <= r.maxDays)
        );
        rate = rateTier?.rate || selectedCar.defaultRatePerDay || 0;
      } else {
        const rateTier = selectedCar.unlimedRates?.find((r: any) => 
          days >= r.minDays && (r.maxDays === null || days <= r.maxDays)
        );
        rate = rateTier?.rate || selectedCar.defaultUnlimitedRatePerDay || 0;
      }
      
      const carTotal = days * rate;
      const extrasTotal = selectedExtras.reduce((sum, extraId) => {
        const extra = extras.find(e => e._id === extraId);
        return sum + (extra?.price || 0);
      }, 0);
      const originalAmount = carTotal + extrasTotal;

      const response = await discountService.validateDiscount({
        discountCode: discountCode.trim(),
        customerId: '', // Will be set when customer is created
        carId: selectedCar._id,
        pickUpDate: formData.pickUpDate,
        returnDate: formData.returnDate,
        pickUpLocation: formData.pickUpLocation,
        rentalDays: days,
        originalAmount: originalAmount,
        bookingId: '' // Not needed for validation
      });

      if (response.success && response.data) {
        if (response.data.isValid) {
          setDiscountInfo(response.data);
          setDiscountError(null);
        } else {
          setDiscountError(response.data.errors?.join(', ') || 'Invalid discount code');
          setDiscountInfo(null);
        }
      } else {
        setDiscountError(response.error?.message || 'Failed to validate discount');
        setDiscountInfo(null);
      }
    } catch (err: any) {
      setDiscountError(err.message || 'Failed to validate discount');
      setDiscountInfo(null);
    } finally {
      setValidatingDiscount(false);
    }
  };

  // Remove discount
  const removeDiscount = () => {
    setDiscountCode('');
    setDiscountInfo(null);
    setDiscountError(null);
  };

  // Get the current rate being used
  const getCurrentRate = () => {
    if (!selectedCar) return 0;
    const days = getDaysCount();
    if (days <= 0) return 0;
    
    if (mileageType === 'limited') {
      const rateTier = selectedCar.rates?.find((r: any) => 
        days >= r.minDays && (r.maxDays === null || days <= r.maxDays)
      );
      return rateTier?.rate || selectedCar.defaultRatePerDay || 0;
    } else {
      const rateTier = selectedCar.unlimedRates?.find((r: any) => 
        days >= r.minDays && (r.maxDays === null || days <= r.maxDays)
      );
      return rateTier?.rate || selectedCar.defaultUnlimitedRatePerDay || 0;
    }
  };

  // Handle extras selection
  const handleExtraToggle = (extraId: string) => {
    setSelectedExtras(prev => {
      if (prev.includes(extraId)) {
        return prev.filter(id => id !== extraId);
      } else {
        return [...prev, extraId];
      }
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const bookingData = {
        customer: {
          firstName: formData.customerFirstName,
          lastName: formData.customerLastName,
          email: formData.customerEmail,
          phone: formData.customerPhone,
          licenseNumber: formData.customerLicenseNumber
        },
        booking: {
          pickUpLocation: formData.pickUpLocation,
          pickUpDate: new Date(`${formData.pickUpDate}T${formData.pickUpTime}`),
          pickUpTime: formData.pickUpTime,
          returnLocation: formData.returnLocation,
          returnDate: new Date(`${formData.returnDate}T${formData.returnTime}`),
          returnTime: formData.returnTime,
          driverAge: Number(formData.driverAge),
          agreeToTerms: true,
          status: BookingStatus.CONFIRMED,
          paymentStatus: formData.paymentStatus,
          paymentMethod: formData.paymentMethod,
          totalAmount: Number(formData.totalAmount),
          paidAmount: Number(formData.paidAmount),
          isManual: true,
          createdBy: 'admin',
          specialRequests: formData.specialRequests,
          internalNotes: formData.internalNotes,
          mileageType: mileageType
        },
        car: { _id: formData.carId },
        rate: { _id: null },
        extras: selectedExtras.map(id => ({ _id: id }))
      };

      const response = await bookingService.createManualBooking(bookingData);
      
      if (response.success && response.data) {
        onSuccess(response.data);
        onClose();
        resetForm();
      } else {
        setError(response.error?.message || 'Failed to create booking');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      customerFirstName: '',
      customerLastName: '',
      customerEmail: '',
      customerPhone: '',
      customerLicenseNumber: '',
      carId: '',
      pickUpDate: '',
      pickUpTime: '09:00',
      returnDate: '',
      returnTime: '09:00',
      pickUpLocation: 'East Perth',
      returnLocation: 'East Perth',
      totalAmount: 0,
      paidAmount: 0,
      paymentMethod: 'counter',
      paymentStatus: PaymentStatus.PENDING,
      driverAge: 25,
      specialRequests: '',
      internalNotes: ''
    });
    setSelectedCar(null);
    setSelectedExtras([]);
    setMileageType('limited');
    setDiscountCode('');
    setDiscountInfo(null);
    setDiscountError(null);
  };

  const getDaysCount = () => {
    if (!formData.pickUpDate || !formData.returnDate) return 0;
    const pickup = new Date(formData.pickUpDate);
    const returnD = new Date(formData.returnDate);
    let days = Math.ceil((returnD.getTime() - pickup.getTime()) / (1000 * 60 * 60 * 24));
    // If same day or 0 days, treat as 1 day rental
    if (days <= 0) days = 1;
    return days;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create Manual Booking" size="xl">
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Customer Information */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">Customer Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                License Number * {lookingUpCustomer && <span className="text-blue-600">(Looking up...)</span>}
              </label>
              <input
                type="text"
                name="customerLicenseNumber"
                value={formData.customerLicenseNumber}
                onChange={handleChange}
                onBlur={handleLicenseBlur}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter license number to auto-fill existing customer"
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter the customer's license number. If they exist in the system, their details will be auto-filled.
              </p>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                name="customerEmail"
                value={formData.customerEmail}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="customer@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                type="text"
                name="customerFirstName"
                value={formData.customerFirstName}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                name="customerLastName"
                value={formData.customerLastName}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone *
              </label>
              <input
                type="tel"
                name="customerPhone"
                value={formData.customerPhone}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Vehicle Selection */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">Vehicle Selection</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vehicle *
              </label>
              <select
                name="carId"
                value={formData.carId}
                onChange={handleCarChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a vehicle</option>
                {vehicles.map(vehicle => (
                  <option key={vehicle._id} value={vehicle._id}>
                    {vehicle.brand} {vehicle.model} - {vehicle.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Show pricing when car is selected */}
            {selectedCar && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <h4 className="font-medium text-gray-900 mb-3">Select Mileage Type:</h4>
                <div className="space-y-2 mb-4">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="mileageType"
                      value="limited"
                      checked={mileageType === 'limited'}
                      onChange={() => setMileageType('limited')}
                      className="h-4 w-4 text-blue-600"
                    />
                    <span className="text-sm font-medium">Limited Mileage</span>
                  </label>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="mileageType"
                      value="unlimited"
                      checked={mileageType === 'unlimited'}
                      onChange={() => setMileageType('unlimited')}
                      className="h-4 w-4 text-blue-600"
                    />
                    <span className="text-sm font-medium">Unlimited Mileage</span>
                  </label>
                </div>

                {/* Pricing Table */}
                <div className="bg-white rounded-md border border-gray-200 overflow-hidden">
                  <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
                    <h5 className="text-sm font-semibold text-gray-700">Pricing Tiers</h5>
                  </div>
                  <div className="divide-y divide-gray-200">
                    <div className="grid grid-cols-3 gap-2 px-4 py-2 bg-gray-50 text-xs font-medium text-gray-600">
                      <div>Duration</div>
                      <div className="text-right">Limited</div>
                      <div className="text-right">Unlimited</div>
                    </div>
                    {selectedCar.rates && selectedCar.rates.length > 0 ? (
                      selectedCar.rates.map((rateTier: any, index: number) => {
                        const unlimitedRate = selectedCar.unlimedRates?.[index];
                        const durationText = rateTier.maxDays === null 
                          ? `${rateTier.minDays}+ days`
                          : rateTier.minDays === rateTier.maxDays
                            ? `${rateTier.minDays} day${rateTier.minDays > 1 ? 's' : ''}`
                            : `${rateTier.minDays}-${rateTier.maxDays} days`;
                        
                        return (
                          <div key={index} className="grid grid-cols-3 gap-2 px-4 py-2 text-sm">
                            <div className="text-gray-700">{durationText}</div>
                            <div className={`text-right font-medium ${mileageType === 'limited' ? 'text-blue-600' : 'text-gray-600'}`}>
                              ${rateTier.rate}/day
                            </div>
                            <div className={`text-right font-medium ${mileageType === 'unlimited' ? 'text-blue-600' : 'text-gray-600'}`}>
                              ${unlimitedRate?.rate || 'N/A'}/day
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-500 text-center">
                        No pricing tiers available
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Extras Selection */}
        {extras.length > 0 && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Add-ons & Extras</h3>
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {extras.map(extra => (
                  <label
                    key={extra._id}
                    className="flex items-start space-x-3 cursor-pointer p-3 bg-white border border-gray-200 rounded-md hover:border-blue-300 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedExtras.includes(extra._id)}
                      onChange={() => handleExtraToggle(extra._id)}
                      className="mt-1 h-4 w-4 text-blue-600 rounded"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <span className="font-medium text-gray-900">{extra.title}</span>
                        <span className="text-blue-600 font-semibold">${extra.price.toFixed(2)}</span>
                      </div>
                      {extra.description && (
                        <p className="text-sm text-gray-500 mt-1">{extra.description}</p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
              {selectedExtras.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Extras Total:</span>
                    <span className="font-semibold text-gray-900">
                      ${selectedExtras.reduce((sum, id) => {
                        const extra = extras.find(e => e._id === id);
                        return sum + (extra?.price || 0);
                      }, 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Booking Details */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">Booking Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pick-up Date *
              </label>
              <input
                type="date"
                name="pickUpDate"
                value={formData.pickUpDate}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pick-up Time *
              </label>
              <input
                type="time"
                name="pickUpTime"
                value={formData.pickUpTime}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pick-up Location *
              </label>
              <select
                name="pickUpLocation"
                value={formData.pickUpLocation}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="East Perth">East Perth</option>
                <option value="Perth Airport">Perth Airport</option>
                <option value="Crown Plaza">Crown Plaza</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Return Date * <span className="text-xs text-gray-500">(Auto-set to +7 days)</span>
              </label>
              <input
                type="date"
                name="returnDate"
                value={formData.returnDate}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Return Time *
              </label>
              <input
                type="time"
                name="returnTime"
                value={formData.returnTime}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Return Location *
              </label>
              <select
                name="returnLocation"
                value={formData.returnLocation}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="East Perth">East Perth</option>
                <option value="Perth Airport">Perth Airport</option>
                <option value="Crown Plaza">Crown Plaza</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Driver Age *
              </label>
              <input
                type="number"
                name="driverAge"
                value={formData.driverAge}
                onChange={handleChange}
                required
                min="18"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Total Amount Display */}
          {formData.totalAmount > 0 && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-md p-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Rental Duration:</span>
                  <span className="font-medium">{getDaysCount()} {getDaysCount() === 1 ? 'day' : 'days'}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Rate ({mileageType} mileage):</span>
                  <span className="font-medium">${getCurrentRate()}/day</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Car Rental:</span>
                  <span className="font-medium">${(getDaysCount() * getCurrentRate()).toFixed(2)}</span>
                </div>
                {selectedExtras.length > 0 && (
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Extras ({selectedExtras.length} items):</span>
                    <span className="font-medium">${selectedExtras.reduce((sum, id) => {
                      const extra = extras.find(e => e._id === id);
                      return sum + (extra?.price || 0);
                    }, 0).toFixed(2)}</span>
                  </div>
                )}
                {discountInfo && discountInfo.isValid && (
                  <>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Subtotal Before Discount:</span>
                      <span className="font-medium">${((getDaysCount() * getCurrentRate()) + selectedExtras.reduce((sum, id) => {
                        const extra = extras.find(e => e._id === id);
                        return sum + (extra?.price || 0);
                      }, 0)).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-green-700 font-medium">
                      <span>Discount ({discountCode}):</span>
                      <span>-${discountInfo.discountAmount?.toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div className="pt-2 border-t border-green-300">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Subtotal (excl. GST):</span>
                    <span className="font-medium">${(formData.totalAmount / 1.1).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>GST (10%):</span>
                    <span className="font-medium">${(formData.totalAmount - (formData.totalAmount / 1.1)).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">Total Amount (incl. GST):</span>
                    <span className="text-2xl font-bold text-green-600">${formData.totalAmount.toFixed(2)}</span>
                  </div>
                  {discountInfo && discountInfo.isValid && (
                    <div className="mt-2 text-sm text-green-700 font-medium text-right">
                      You save ${discountInfo.discountAmount?.toFixed(2)}!
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Discount Code */}
        {formData.totalAmount > 0 && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Discount Code (Optional)</h3>
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
              {!discountInfo ? (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                      placeholder="Enter discount code"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={validatingDiscount}
                    />
                    <button
                      type="button"
                      onClick={validateDiscountCode}
                      disabled={validatingDiscount || !discountCode.trim()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {validatingDiscount ? 'Validating...' : 'Apply'}
                    </button>
                  </div>
                  {discountError && (
                    <div className="text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {discountError}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-md p-3">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <div className="font-medium text-green-900">
                          Discount Applied: {discountCode}
                        </div>
                        <div className="text-sm text-green-700">
                          {discountInfo.discount?.name} - Saving ${discountInfo.discountAmount?.toFixed(2)}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removeDiscount}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payment Information */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">Payment Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Paid Amount
              </label>
              <input
                type="number"
                name="paidAmount"
                value={formData.paidAmount}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Method
              </label>
              <select
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="counter">Counter</option>
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="bank_transfer">Bank Transfer</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Status
              </label>
              <select
                name="paymentStatus"
                value={formData.paymentStatus}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={PaymentStatus.PENDING}>Pending</option>
                <option value={PaymentStatus.PAID}>Paid</option>
                <option value={PaymentStatus.PARTIAL}>Partial</option>
              </select>
            </div>
          </div>
        </div>

        {/* Additional Notes */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">Additional Information</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Special Requests
              </label>
              <textarea
                name="specialRequests"
                value={formData.specialRequests}
                onChange={handleChange}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Internal Notes
              </label>
              <textarea
                name="internalNotes"
                value={formData.internalNotes}
                onChange={handleChange}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Booking'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ManualBookingForm;
